import type { CSSProperties, JSX, ReactNode } from "react";
import { useId, useState } from "react";
import { cx } from "../cx";

export type RadioOption = string | { value: string; label: ReactNode };

export interface RadioProps {
  /** Options as strings or `{ value, label }` pairs. */
  options?: ReadonlyArray<RadioOption> | undefined;
  /** Controlled selected value. */
  value?: string | undefined;
  /** Uncontrolled initial value. */
  defaultValue?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  /** Shared input name; defaults to a generated one. */
  name?: string | undefined;
  disabled?: boolean | undefined;
  /** Accessible name for the radiogroup. */
  "aria-label"?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Vertical radio group backed by real inputs (native arrow-key movement).
 * Ported from design/tarski-kit/components/forms/Radio.jsx.
 */
export function Radio({
  options = [],
  value,
  defaultValue,
  onChange,
  name,
  disabled = false,
  "aria-label": ariaLabel,
  className,
  style,
}: RadioProps): JSX.Element {
  const [internal, setInternal] = useState<string | undefined>(defaultValue);
  const current = value !== undefined ? value : internal;
  const fallbackName = useId();
  const groupName = name ?? fallbackName;

  const pick = (next: string) => {
    if (value === undefined) {
      setInternal(next);
    }
    onChange?.(next);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cx("tk-radio", disabled && "tk-radio--disabled", className)}
      style={style}
    >
      {options.map((option) => {
        const item = typeof option === "string" ? { value: option, label: option } : option;
        return (
          <label key={item.value} className="tk-radio__option">
            <input
              type="radio"
              className="tk-radio__input"
              name={groupName}
              value={item.value}
              checked={current === item.value}
              onChange={() => pick(item.value)}
              disabled={disabled}
            />
            <span className="tk-radio__control" aria-hidden="true">
              <span className="tk-radio__dot" />
            </span>
            <span className="tk-radio__label">{item.label}</span>
          </label>
        );
      })}
    </div>
  );
}
